<template>
	<div class="integration-form-fields">
		<div class="integration-form-container">
			<!-- Left side - All Form Content -->
			<div class="integration-form-main">
				<QuestionGroup>
					<!-- System Selection Question -->
					<QuestionCard
						v-if="cloneMode"
						title="Which integration system should this service use?"
						description="Choose the integration system that this service is a part of. This determines where your data will be sent and how it will be authenticated."
					>
						<v-row dense>
							<v-col cols="6">
								<Dropdown
									label="System *"
									:value="integrationId"
									:items="systemIntegrationItems"
									searchable
									rules="required"
									class="system-dropdown"
									:disabled="!userFullPermissions"
									@input="$emit('update:integrationId', $event)"
								/>
							</v-col>
						</v-row>
					</QuestionCard>

					<!-- HTTP Configuration Question -->
					<QuestionCard
						title="How should this service connect to the API?"
						:description="httpConfigDescription"
					>
						<div class="d-flex flex-row cassie-horizontal-sm http-config-row">
							<Dropdown
								label="HTTP Method *"
								:value="httpMethod"
								:items="httpMethodItems"
								rules="required"
								class="cassie-input-width-sm http-method-field"
								:disabled="!userFullPermissions"
								@input="$emit('update:httpMethod', $event)"
							/>
							<DropZone
								context="url"
								:field-usage-map="fieldUsageMap"
								class="endpoint-field-container"
								@field-drop="handleFieldDrop"
							>
								<TextField
									label="Endpoint Sub-Address *"
									:value="requestUrl"
									rules="required"
									class="endpoint-field"
									:disabled="!userFullPermissions"
									@input="$emit('update:requestUrl', $event)"
								/>
							</DropZone>
						</div>
						<v-alert
							v-if="isGetMethod"
							prominent
							color="primary"
							border="left"
							colored-border
							elevation="2"
							class="get-info-banner mt-4 mb-0"
							:icon="false"
						>
							<v-row align="center">
								<v-col class="shrink ml-3">
									<v-icon
										size="24"
										color="primary"
									>
										mdi-information
									</v-icon>
								</v-col>
								<v-col class="grow pl-2">
									<div class="text-subtitle-2">
										GET requests have no request payload
									</div>
									<div class="text-caption">
										Put record values in the endpoint sub-address, for example /contacts?email=[PLACEHOLDER_EMAIL]. Placeholder values are URL-encoded automatically; JavaScript URL templates should use encode.url(). The Request Payload Mapping step is skipped for GET; you can still open it from the step list.
									</div>
								</v-col>
							</v-row>
						</v-alert>
					</QuestionCard>

					<!-- Custom Headers Section -->
					<CustomHeadersTable
						:custom-headers="customHeaders"
						:user-full-permissions="userFullPermissions"
						:field-usage-map="fieldUsageMap"
						:is-no-auth-credentials="isNoAuthCredentials"
						@update:customHeaders="$emit('update:customHeaders', $event)"
					/>
					<!-- Request Throttling Section -->
					<RequestThrottlingSection
						:request-throttling-wait-time-ms="requestThrottlingWaitTimeMs"
						:user-full-permissions="userFullPermissions"
						@update:requestThrottlingWaitTimeMs="$emit('update:requestThrottlingWaitTimeMs', $event)"
					/>
				</QuestionGroup>
			</div>
		</div>
	</div>
</template>

<script>

import TextField from '../../../../../../../shared/components/text-field.vue'
import Dropdown from '../../../../../../../shared/components/dropdown.vue'
import DropZone from './DropZone.vue'
import CustomHeadersTable from './CustomHeadersTable.vue'
import RequestThrottlingSection from './RequestThrottlingSection.vue'
import { QuestionCard, QuestionGroup } from '../../../../../components/component-library-wrapper.js'

export default {
	name: 'integration-form-fields',
	components: {
		TextField,
		Dropdown,
		DropZone,
		CustomHeadersTable,
		RequestThrottlingSection,
		QuestionCard,
		QuestionGroup
	},
	props: {
		httpMethod: String,
		requestUrl: String,
		requestThrottlingWaitTimeMs: [String, Number],
		customHeaders: {
			type: Array,
			default: () => []
		},
		httpMethodItems: Array,
		userFullPermissions: Boolean,
		fieldUsageMap: {
			type: Object,
			default: () => ({})
		},
		isNoAuthCredentials: {
			type: Boolean,
			default: false
		},

		// System Integration Props
		systemIntegrationItems: Array,
		integrationId: Number,
		cloneMode: Boolean
	},
	computed: {
		isGetMethod () {
			return (this.httpMethod || '').toUpperCase() === 'GET'
		},
		httpConfigDescription () {
			return this.isGetMethod
				? 'Configure the HTTP method and endpoint URL used to retrieve data from your integration system.'
				: 'Configure the HTTP method and endpoint URL that will be used to send data to your integration system.'
		}
	},
	methods: {
		handleFieldDrop (dropData) {
			this.$emit('field-drop', dropData)
		}
	}
}
</script>

<style lang="scss" scoped>
.integration-form-fields {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
}

.integration-form-container {
  width: 100%;
}

.integration-form-main {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
}

.help-note {
	font-size: 13px;
	color: var(--global-muted-foreground);
	font-style: italic;
	margin: 0;
	line-height: 1.4;
}

.request-body-section {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.request-body-header {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

.request-body-title {
  font-size: 16px;
  font-weight: 500;
  color: var(--global-foreground);
  margin: 0;
}

.http-config-row {
  align-items: flex-start;
  margin-bottom: 8px;
}

.http-method-field {
  flex-shrink: 0;
  max-width: 120px;
}

.endpoint-field-container {
  flex: 1;
  min-width: 0;
}

.endpoint-field {
  width: 100%;
}

// Request throttling field with tooltip
.request-throttling-field {
  display: flex;
  align-items: center;
  gap: 8px;
}

// Match the subtle JsonEditor styling from storybook
.request-body-section :deep(.json-editor) {
  min-height: 300px;
  width: 100%;
}

.request-body-section :deep(.json-editor .CodeMirror) {
  font-family: 'SF Mono', 'Monaco', 'Inconsolata', 'Roboto Mono', 'Consolas', 'Menlo', 'DejaVu Sans Mono', 'Liberation Mono', monospace !important;
  font-size: 13px !important;
  line-height: 1.5 !important;
  font-weight: 400 !important;
}

.request-body-section :deep(.json-editor .CodeMirror-line) {
  font-family: 'SF Mono', 'Monaco', 'Inconsolata', 'Roboto Mono', 'Consolas', 'Menlo', 'DejaVu Sans Mono', 'Liberation Mono', monospace !important;
}

.request-body-section :deep(.json-editor .CodeMirror pre) {
  font-family: 'SF Mono', 'Monaco', 'Inconsolata', 'Roboto Mono', 'Consolas', 'Menlo', 'DejaVu Sans Mono', 'Liberation Mono', monospace !important;
}

.request-body-section :deep(.json-editor__container) {
  border: 1px solid var(--global-border-color);
  border-radius: var(--radius-sm, 4px);
  width: 100%;
  background: var(--global-main-background-color);

  &:hover {
    border-color: var(--global-border-color);
  }

  &:focus-within {
    border-color: var(--global-color-blue-darken-2);
    box-shadow: 0 0 0 1px var(--global-color-blue-darken-2);
  }
}

// Custom validation error styling - use brand colors instead of red
:deep(.v-text-field.error--text) {
	.v-label {
		color: var(--global-color-orange-darken-3) !important; // Amber color instead of red
	}

	.v-input__control .v-input__slot {
		border-color: var(--global-color-orange-darken-3) !important; // Amber border
	}

	.v-messages__message {
		color: #92400e !important; // Darker amber for message text
		font-weight: 500;
	}
}

:deep(.v-select.error--text) {
	.v-label {
		color: var(--global-color-orange-darken-3) !important;
	}

	.v-input__control .v-input__slot {
		border-color: var(--global-color-orange-darken-3) !important;
	}

	.v-messages__message {
		color: #92400e !important;
		font-weight: 500;
	}
}

/* Form Controls - Match DataSelection dropdown widths */
.system-dropdown {
	// max-width: 300px;
}

.get-info-banner {
	background-color: var(--global-card-background, #ffffff) !important;
}
.get-info-banner .text-subtitle-2,
.get-info-banner .text-caption {
	color: var(--global-foreground, #1f2937);
}
</style>
